import React, { useEffect, useMemo, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, Download, Upload, FileSpreadsheet,
    X, Search, Users, FileDown, CheckCircle, XCircle, AlertCircle, RefreshCw, Eye, Pencil, Save
} from "lucide-react";
import ScrollToTop from "../../Ui/UiJs/ScrollToTop";
import "./UserMassImport.css";

import {
    getAllUsersRequest,
    downloadUsersTemplateRequest,
    reviewUsersMassImportRequest,
    importUsersMassRequest,
    exportUsersRequest,
    updateUserRequest,
    uploadAttestationRibRequest,
} from "../../redux/actions/userActions";
import { getAllJobsRequest } from "../../redux/actions/Jobactions";
import { getAllEntitesRequest } from "../../redux/actions/entitesActions";
import axios from "axios";
import { API_BASE_URL } from "../../../api";

const Modal = ({ open, title, onClose, footer, children }) => {
    if (!open) return null;
    return (
        <div className="um-overlay" onMouseDown={onClose}>
            <div className="um-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="um-modal-header">
                    <h3>{title}</h3>
                    <button className="um-modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="um-modal-body">{children}</div>
                {footer && <div className="um-modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

const emptyUserForm = {
    employeeCode: "",
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    nationality: "",
    genre: "",
    qualification: "",
    numBadge: "",
    cnss: false,
    numCnss: "",
    situationCnss: "NON_RENSEIGNE",
    numMutuelle: "",
    numRetraite: "",
    typePaie: "",
    modePaiement: "",
    dateOfBirth: "",
    hireDate: "",
    jobId: "",
    entityId: "",
    status: "ACTIVE",
    isActive: true,
    profilePictureUrl: "",
    bannerPictureUrl: "",
    city: "",
    bio: "",
    cin: "",
    passeport: "",
    carteSejour: "",
    phoneUrgence: "",
    adresse: "",
    codePostal: "",
    statutMarital: "",
    nombreEnfants: "",
    personneUrgence: "",
    rib: "",
    attestationRib: "",
    attestationRibFile: null,
    iban: "",
    typeContratId: "",
    category: "",
};

const userFullName = (user) =>
    user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Utilisateur";

const isUserActive = (user) =>
    user?.isActive !== false && user?.status !== "INACTIVE";

const toInputDate = (value) => value ? String(value).slice(0, 10) : "";

const makeUserForm = (user = {}) => ({
    ...emptyUserForm,
    employeeCode: user.employeeCode || "",
    username: user.username || "",
    email: user.email || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    phone: user.phone || "",
    nationality: user.nationality || "",
    genre: user.genre || "",
    qualification: user.qualification || "",
    numBadge: user.numBadge || "",
    cnss: user.cnss === true,
    numCnss: user.numCnss || "",
    situationCnss: user.cnss === true ? (user.situationCnss || "NON_RENSEIGNE") : "NON_RENSEIGNE",
    numMutuelle: user.numMutuelle || "",
    numRetraite: user.numRetraite || "",
    typePaie: user.typePaie || "",
    modePaiement: user.modePaiement || "",
    dateOfBirth: toInputDate(user.dateOfBirth),
    hireDate: toInputDate(user.hireDate),
    jobId: user.jobId || "",
    entityId: user.entityId || "",
    status: user.status || "ACTIVE",
    isActive: user.isActive !== false,
    profilePictureUrl: user.profilePictureUrl || "",
    bannerPictureUrl: user.bannerPictureUrl || "",
    city: user.city || "",
    bio: user.bio || "",
    cin: user.cin || "",
    passeport: user.passeport || "",
    carteSejour: user.carteSejour || "",
    phoneUrgence: user.phoneUrgence || "",
    adresse: user.adresse || "",
    codePostal: user.codePostal || "",
    statutMarital: user.statutMarital || "",
    nombreEnfants: user.nombreEnfants != null ? String(user.nombreEnfants) : "",
    personneUrgence: user.personneUrgence || "",
    rib: user.rib || "",
    attestationRib: user.attestationRib || "",
    attestationRibFile: null,
    iban: user.iban || "",
    typeContratId: user.typeContratId || "",
    category: user.category || "",
});

const DetailItem = ({ label, value }) => (
    <div className="um-detail-item">
        <span>{label}</span>
        <strong>{value || "—"}</strong>
    </div>
);

const SITUATION_CNSS_OPTIONS = [
    { value: "NON_RENSEIGNE", label: "Non renseigné" },
    { value: "SORTANT", label: "Sortant" },
    { value: "DECEDE", label: "Décédé" },
    { value: "MATERNITE", label: "Maternité" },
    { value: "MALADIE", label: "Maladie" },
    { value: "ACCIDENT_DE_TRAVAIL", label: "Accident de travail" },
    { value: "CONGE_SANS_SALAIRE", label: "Congé sans salaire" },
    { value: "MAINTENU_SANS_SALAIRE", label: "Maintenu sans salaire" },
    { value: "MALADIE_PROFESSIONNEL", label: "Maladie professionnel" },
];

const TYPE_PAIE_OPTIONS = [
    { value: "MENSUEL", label: "Mensuel" },
    { value: "JOURNALIER", label: "Journalier" },
    { value: "HORAIRE", label: "Horaire" },
    { value: "A_LA_PIECE", label: "À la pièce" },
];

const MODE_PAIEMENT_OPTIONS = [
    { value: "VIREMENT", label: "Virement" },
    { value: "CHEQUE", label: "Chèque" },
    { value: "ESPECES", label: "Espèces" },
];

const optionLabel = (options, value) =>
    options.find((option) => option.value === value)?.label || value;

export default function UserMassImport() {
    const dispatch = useDispatch();
    const { users, loading, massImportReview } = useSelector((s) => s.user);
    const jobs = useSelector((s) => s.jobs?.jobs || []);
    const entites = useSelector((s) => s.entite?.entites || []);
    const currentUser = useSelector((state) => state?.auth?.user);
    const currentCompanyId = currentUser?.companyId;

    const [typesContrats, setTypesContrats] = useState([]);

    const getHeaders = useCallback(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    const fetchTypesContrats = useCallback(async () => {
        try {
            const resp = await axios.get(`${API_BASE_URL}/api/types-contrats/actifs`, { headers: getHeaders() });
            setTypesContrats(resp.data || []);
        } catch (e) {
            // silencieux
        }
    }, [getHeaders]);

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [bulkAction, setBulkAction] = useState("add");
    const [rows, setRows] = useState([]);
    const [existingQuery, setExistingQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [detailUser, setDetailUser] = useState(null);
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState(emptyUserForm);

    const refreshData = () => dispatch(getAllUsersRequest());
    useEffect(() => {
        refreshData();
        dispatch(getAllJobsRequest());
        dispatch(getAllEntitesRequest());
        fetchTypesContrats();
    }, [dispatch, fetchTypesContrats]);

    const openDetail = (user) => setDetailUser(user);

    const openEdit = (user) => {
        setEditUser(user);
        setEditForm(makeUserForm(user));
    };

    const closeEdit = () => {
        setEditUser(null);
        setEditForm(emptyUserForm);
    };

    const setEditValue = (key, value) => {
        setEditForm((prev) => {
            if (key === "cnss" && value === false) {
                return { ...prev, cnss: false, situationCnss: "NON_RENSEIGNE" };
            }
            return { ...prev, [key]: value };
        });
    };

    const userToPayload = (form) => ({
        employeeCode: form.employeeCode || null,
        username: form.username || form.email || null,
        email: form.email || null,
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        phone: form.phone || null,
        nationality: form.nationality || null,
        genre: form.genre || null,
        qualification: form.qualification || null,
        numBadge: form.numBadge || null,
        cnss: !!form.cnss,
        numCnss: form.numCnss || null,
        situationCnss: form.cnss ? (form.situationCnss || "NON_RENSEIGNE") : "NON_RENSEIGNE",
        numMutuelle: form.numMutuelle || null,
        numRetraite: form.numRetraite || null,
        typePaie: form.typePaie || null,
        modePaiement: form.modePaiement || null,
        dateOfBirth: form.dateOfBirth || null,
        hireDate: form.hireDate || null,
        jobId: form.jobId ? Number(form.jobId) : null,
        entityId: form.entityId ? Number(form.entityId) : null,
        status: form.status || "ACTIVE",
        isActive: !!form.isActive,
        profilePictureUrl: form.profilePictureUrl || null,
        bannerPictureUrl: form.bannerPictureUrl || null,
        city: form.city || null,
        bio: form.bio || null,
        cin: form.cin || null,
        passeport: form.passeport || null,
        carteSejour: form.carteSejour || null,
        phoneUrgence: form.phoneUrgence || null,
        adresse: form.adresse || null,
        codePostal: form.codePostal || null,
        statutMarital: form.statutMarital || null,
        nombreEnfants: form.nombreEnfants !== "" ? Number(form.nombreEnfants) : null,
        personneUrgence: form.personneUrgence || null,
        rib: form.rib || null,
        attestationRib: form.attestationRib || null,
        iban: form.iban || null,
        typeContratId: form.typeContratId ? Number(form.typeContratId) : null,
        category: form.category || null,
    });

    const onSaveUser = () => {
        if (!editUser?.id) return;
        Swal.fire({
            title: "Confirmer la modification",
            text: "Êtes-vous sur de vouloir modifier les informations de cet utilisateur ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2a5ca9",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, modifier",
            cancelButtonText: "Annuler",
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(updateUserRequest(editUser.id, userToPayload(editForm)));
                if (editForm.attestationRibFile) {
                    dispatch(uploadAttestationRibRequest(editUser.id, editForm.attestationRibFile));
                }
                closeEdit();
                Swal.fire({
                    icon: "success",
                    title: "Utilisateur mis à jour",
                    timer: 1800,
                    showConfirmButton: false,
                });
            }
        });
    };

    const handleFile = async (file) => {
        if (!file) return;
        setFileName(file.name);
        setBulkFile(file);
        const data = await file.arrayBuffer();
        const wb = XLSX.read(data, { type: "array", cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const mapped = json.map((r) => ({
            employeeCode: String(r.employeeCode || r.Matricule || "").trim() || null,
            username: String(r.username || "").trim() || null,
            email: String(r.email || "").trim() || null,
            firstName: String(r.firstName || r.Prenom || "").trim() || null,
            lastName: String(r.lastName || r.Nom || "").trim() || null,
            jobCode: String(r.jobCode || "").trim() || null,
            entityCode: String(r.entityCode || "").trim() || null,
            typeContrat: String(r.typeContrat || "").trim() || null,
            remunerationRib: String(r.remunerationRib || r.RIB || r.ribRemuneration || "").trim() || null,
            remunerationBanque: String(r.remunerationBanque || r.Banque || r.banque || "").trim() || null,
            remunerationAgence: String(r.remunerationAgence || r.Agence || r.agence || "").trim() || null,
            remunerationTaux: r.remunerationTaux || r.Taux || r.taux || null,
            enabled: true
        })).filter((r) => r.employeeCode);
        setRows(mapped);
    };

    const filteredExisting = useMemo(() => {
        const q = existingQuery.trim().toLowerCase();
        return (users || []).filter((u) => {
            const matchesQ = !q || (u.employeeCode || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || userFullName(u).toLowerCase().includes(q);
            const matchesF = filter === "all" ? true : (filter === "active" ? isUserActive(u) : !isUserActive(u));
            return matchesQ && matchesF;
        });
    }, [users, existingQuery, filter]);

    const onImport = () => {
        if (!currentCompanyId || !rows.length || hasErrors) return;
        Swal.fire({
            title: "Confirmer l'import", text: `Importer ${rows.length} utilisateur(s) ?`, icon: "question",
            showCancelButton: true, confirmButtonColor: "#2a5ca9", confirmButtonText: "Oui, importer"
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(importUsersMassRequest(currentCompanyId, bulkAction, rows));
                setBulkOpen(false);
            }
        });
    };

    const hasErrors = (massImportReview?.errors || []).length > 0;

    return (
        <div className="um-page">
            <div className="um-header">
                <div>
                    <h1>Liste des utilisateurs</h1>
                    <p>Gérez vos collaborateurs et leurs accès en masse</p>
                </div>
                <div className="um-header-actions">
                    <button className="um-btn um-btn-outline" onClick={() => dispatch(downloadUsersTemplateRequest())}>
                        <Download size={14} /> Template
                    </button>
                    <button className="um-btn um-btn-outline" onClick={() => dispatch(exportUsersRequest())}>
                        <FileDown size={14} /> Exporter
                    </button>
                    <button className="um-btn um-btn-primary" onClick={() => setBulkOpen(true)}>
                        <Upload size={14} /> Import massif
                    </button>
                </div>
            </div>

            <div className="um-toolbar">
                <div className="um-search-wrapper">
                    <Search className="um-search-icon" size={14} />
                    <input className="um-search" placeholder="Rechercher par matricule, nom..." value={existingQuery} onChange={(e) => setExistingQuery(e.target.value)} />
                </div>
                <div className="um-filter-tabs">
                    {[{ k: "all", l: "Tous" }, { k: "active", l: "Actifs" }, { k: "inactive", l: "Inactifs" }].map((f) => (
                        <button key={f.k} className={`um-filter-tab ${filter === f.k ? 'active' : ''}`} onClick={() => setFilter(f.k)}>{f.l}</button>
                    ))}
                </div>
                <div className="um-count">{filteredExisting.length} utilisateur{filteredExisting.length > 1 ? "s" : ""}</div>
            </div>

            <div className="um-table-wrapper">
                <table className="um-table">
                    <thead>
                    <tr>
                        <th>Matricule</th>
                        <th>Nom complet</th>
                        <th>Email</th>
                        <th>Poste</th>
                        <th>Entité</th>
                        <th>Contrat</th>
                        <th>Statut</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr><td colSpan={8}><div className="um-loading"><div className="um-spinner" /><span>Chargement...</span></div></td></tr>
                    ) : filteredExisting.length === 0 ? (
                        <tr><td colSpan={8} className="um-empty">Aucun utilisateur trouvé</td></tr>
                    ) : filteredExisting.slice(0, 50).map((u) => (
                        <tr key={u.id}>
                            <td><span className="um-strong">{u.employeeCode || "—"}</span></td>
                            <td className="um-name">{userFullName(u)}</td>
                            <td className="um-muted">{u.email || "—"}</td>
                            <td className="um-muted">{u.jobName || "—"}</td>
                            <td className="um-muted">{u.entityName || "—"}</td>
                            <td><span className="um-badge um-badge-blue">{u.typeContrat || "—"}</span></td>
                            <td><span className={`um-badge ${isUserActive(u) ? 'um-badge-green' : 'um-badge-grey'}`}>{isUserActive(u) ? "Actif" : "Inactif"}</span></td>
                            <td>
                                <div className="um-row-actions">
                                    <button type="button" className="um-icon-btn" onClick={() => openDetail(u)} title="Afficher les details" aria-label="Afficher les details">
                                        <Eye size={15} />
                                    </button>
                                    <button type="button" className="um-icon-btn um-icon-btn-primary" onClick={() => openEdit(u)} title="Modifier" aria-label="Modifier">
                                        <Pencil size={15} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <Modal open={!!detailUser} title={`Details - ${userFullName(detailUser)}`} onClose={() => setDetailUser(null)}>
                {detailUser && (
                    <div className="um-detail-grid">
                        <DetailItem label="Matricule" value={detailUser.employeeCode} />
                        <DetailItem label="Username" value={detailUser.username} />
                        <DetailItem label="Email" value={detailUser.email} />
                        <DetailItem label="Telephone" value={detailUser.phone} />
                        <DetailItem label="Nationalité" value={detailUser.nationality} />
                        <DetailItem label="Genre" value={detailUser.genre} />
                        <DetailItem label="Qualification" value={detailUser.qualification} />
                        <DetailItem label="N° badge" value={detailUser.numBadge} />
                        <DetailItem label="Poste" value={detailUser.jobName} />
                        <DetailItem label="Entite" value={detailUser.entityName} />
                        <DetailItem label="Statut" value={detailUser.status} />
                        <DetailItem label="Actif" value={isUserActive(detailUser) ? "Oui" : "Non"} />
                        <DetailItem label="Date naissance" value={detailUser.dateOfBirth} />
                        <DetailItem label="Date embauche" value={detailUser.hireDate} />
                        <DetailItem label="CIN" value={detailUser.cin} />
                        <DetailItem label="Passeport" value={detailUser.passeport} />
                        <DetailItem label="Carte sejour" value={detailUser.carteSejour} />
                        <DetailItem label="Ville" value={detailUser.city} />
                        <DetailItem label="Adresse" value={detailUser.adresse} />
                        <DetailItem label="Code postal" value={detailUser.codePostal} />
                        <DetailItem label="Statut marital" value={detailUser.statutMarital} />
                        <DetailItem label="Nombre d'enfants" value={detailUser.nombreEnfants} />
                        <DetailItem label="Personne d'urgence" value={detailUser.personneUrgence} />
                        <DetailItem label="Téléphone d'urgence" value={detailUser.phoneUrgence} />
                        <DetailItem label="RIB" value={detailUser.rib} />
                        <DetailItem label="Attestation RIB" value={detailUser.attestationRib} />
                        <DetailItem label="IBAN" value={detailUser.iban} />
                        <DetailItem label="CNSS" value={detailUser.cnss ? "Oui" : "Non"} />
                        <DetailItem label="N° CNSS" value={detailUser.numCnss} />
                        <DetailItem label="Situation CNSS" value={optionLabel(SITUATION_CNSS_OPTIONS, detailUser.situationCnss)} />
                        <DetailItem label="N° mutuelle" value={detailUser.numMutuelle} />
                        <DetailItem label="N° retraite" value={detailUser.numRetraite} />
                        <DetailItem label="Type paie" value={optionLabel(TYPE_PAIE_OPTIONS, detailUser.typePaie)} />
                        <DetailItem label="Mode paiement" value={optionLabel(MODE_PAIEMENT_OPTIONS, detailUser.modePaiement)} />
                        <DetailItem label="Contrat" value={detailUser.typeContrat} />
                        <DetailItem label="Catégorie" value={detailUser.category} />
                    </div>
                )}
            </Modal>

            <Modal open={!!editUser} title={`Modifier - ${userFullName(editUser)}`} onClose={closeEdit} footer={
                <>
                    <button className="um-btn um-btn-outline" onClick={closeEdit}>Annuler</button>
                    <button className="um-btn um-btn-primary" onClick={onSaveUser}>
                        <Save size={14} /> Enregistrer
                    </button>
                </>
            }>
                <div className="um-form-section">
                    <h3 className="um-form-title">Identité</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Matricule</label>
                            <input value={editForm.employeeCode} onChange={(e) => setEditValue("employeeCode", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Username</label>
                            <input value={editForm.username} onChange={(e) => setEditValue("username", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Prénom</label>
                            <input value={editForm.firstName} onChange={(e) => setEditValue("firstName", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Nom</label>
                            <input value={editForm.lastName} onChange={(e) => setEditValue("lastName", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Email</label>
                            <input type="email" value={editForm.email} onChange={(e) => setEditValue("email", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Téléphone</label>
                            <input value={editForm.phone} onChange={(e) => setEditValue("phone", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Nationalité</label>
                            <input value={editForm.nationality} onChange={(e) => setEditValue("nationality", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Genre</label>
                            <select value={editForm.genre} onChange={(e) => setEditValue("genre", e.target.value)}>
                                <option value="">--</option>
                                <option value="H">H</option>
                                <option value="F">F</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations RH</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Date d'embauche</label>
                            <input type="date" value={editForm.hireDate} onChange={(e) => setEditValue("hireDate", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Date de naissance</label>
                            <input type="date" value={editForm.dateOfBirth} onChange={(e) => setEditValue("dateOfBirth", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Poste</label>
                            <select value={editForm.jobId} onChange={(e) => setEditValue("jobId", e.target.value)}>
                                <option value="">-- Aucun poste --</option>
                                {(jobs || []).map((job) => (
                                    <option key={job.id} value={job.id}>
                                        {[job.code, job.name].filter(Boolean).join(" - ") || `Poste #${job.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Entité</label>
                            <select value={editForm.entityId} onChange={(e) => setEditValue("entityId", e.target.value)}>
                                <option value="">-- Aucune entite --</option>
                                {(entites || []).map((entite) => (
                                    <option key={entite.id} value={entite.id}>
                                        {[entite.code, entite.name].filter(Boolean).join(" - ") || `Entite #${entite.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Statut</label>
                            <select value={editForm.status} onChange={(e) => setEditValue("status", e.target.value)}>
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="INACTIVE">INACTIVE</option>
                                <option value="SUSPENDED">SUSPENDED</option>
                                <option value="PENDING">PENDING</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Compte actif</label>
                            <select value={editForm.isActive ? "true" : "false"} onChange={(e) => setEditValue("isActive", e.target.value === "true")}>
                                <option value="true">Oui</option>
                                <option value="false">Non</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Type contrat</label>
                            <select value={editForm.typeContratId} onChange={(e) => setEditValue("typeContratId", e.target.value)}>
                                <option value="">--</option>
                                {typesContrats.map((tc) => (
                                    <option key={tc.id} value={tc.id}>{tc.code} - {tc.libelle}</option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Categorie</label>
                            <select value={editForm.category} onChange={(e) => setEditValue("category", e.target.value)}>
                                <option value="">--</option>
                                <option value="POSTE_FIXE">Poste fixe</option>
                                <option value="MENSUEL">Mensuel</option>
                                <option value="HORAIRE">Horaire</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Qualification</label>
                            <input value={editForm.qualification} onChange={(e) => setEditValue("qualification", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>N° badge</label>
                            <input value={editForm.numBadge} onChange={(e) => setEditValue("numBadge", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Type paie</label>
                            <select value={editForm.typePaie} onChange={(e) => setEditValue("typePaie", e.target.value)}>
                                <option value="">--</option>
                                {TYPE_PAIE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Mode paiement</label>
                            <select value={editForm.modePaiement} onChange={(e) => setEditValue("modePaiement", e.target.value)}>
                                <option value="">--</option>
                                {MODE_PAIEMENT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations personnelles</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>CIN</label>
                            <input value={editForm.cin} onChange={(e) => setEditValue("cin", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Passeport</label>
                            <input value={editForm.passeport} onChange={(e) => setEditValue("passeport", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Carte sejour</label>
                            <input value={editForm.carteSejour} onChange={(e) => setEditValue("carteSejour", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Statut marital</label>
                            <input value={editForm.statutMarital} onChange={(e) => setEditValue("statutMarital", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Nombre d'enfants</label>
                            <input type="number" min="0" value={editForm.nombreEnfants} onChange={(e) => setEditValue("nombreEnfants", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Ville</label>
                            <input value={editForm.city} onChange={(e) => setEditValue("city", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Adresse</label>
                            <input value={editForm.adresse} onChange={(e) => setEditValue("adresse", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Code postal</label>
                            <input value={editForm.codePostal} onChange={(e) => setEditValue("codePostal", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Telephone urgence</label>
                            <input value={editForm.phoneUrgence} onChange={(e) => setEditValue("phoneUrgence", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Personne urgence</label>
                            <input value={editForm.personneUrgence} onChange={(e) => setEditValue("personneUrgence", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Bio</label>
                            <textarea rows={3} value={editForm.bio} onChange={(e) => setEditValue("bio", e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">CNSS et affiliations</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>CNSS</label>
                            <select value={editForm.cnss ? "true" : "false"} onChange={(e) => setEditValue("cnss", e.target.value === "true")}>
                                <option value="true">Oui</option>
                                <option value="false">Non</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>N° CNSS</label>
                            <input value={editForm.numCnss} onChange={(e) => setEditValue("numCnss", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Situation CNSS</label>
                            <select
                                value={editForm.cnss ? editForm.situationCnss : "NON_RENSEIGNE"}
                                onChange={(e) => setEditValue("situationCnss", e.target.value)}
                                disabled={!editForm.cnss}
                            >
                                {SITUATION_CNSS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>N° mutuelle</label>
                            <input value={editForm.numMutuelle} onChange={(e) => setEditValue("numMutuelle", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>N° retraite</label>
                            <input value={editForm.numRetraite} onChange={(e) => setEditValue("numRetraite", e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations bancaires</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Attestation de RIB (PDF)</label>
                            <input type="file" accept="application/pdf,.pdf" onChange={(e) => setEditValue("attestationRibFile", e.target.files?.[0] || null)} />
                            {editForm.attestationRib && !editForm.attestationRibFile && <small>{editForm.attestationRib}</small>}
                        </div>
                        <div className="um-field">
                            <label>RIB</label>
                            <input value={editForm.rib} onChange={(e) => setEditValue("rib", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>IBAN</label>
                            <input value={editForm.iban} onChange={(e) => setEditValue("iban", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Photo profil</label>
                            <input value={editForm.profilePictureUrl} onChange={(e) => setEditValue("profilePictureUrl", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Bannière</label>
                            <input value={editForm.bannerPictureUrl} onChange={(e) => setEditValue("bannerPictureUrl", e.target.value)} />
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal open={bulkOpen} title="Importation massive (Excel)" onClose={() => setBulkOpen(false)} footer={
                <>
                    <button className="um-btn um-btn-outline" onClick={() => setBulkOpen(false)}>Annuler</button>
                    <button className="um-btn um-btn-primary" onClick={onImport} disabled={!massImportReview || hasErrors}>Confirmer</button>
                </>
            }>
                <div className="um-form-section">
                    <h3 className="um-form-title">Paramètres</h3>
                    <div className="um-form-grid">
                        <div className="um-field um-field-full">
                            <label>Action</label>
                            <div className="um-segmented">
                                <button className={`um-seg-btn ${bulkAction === "add" ? "active" : ""}`} onClick={() => setBulkAction("add")}>Ajouter</button>
                                <button className={`um-seg-btn ${bulkAction === "edit" ? "active" : ""}`} onClick={() => setBulkAction("edit")}>Mettre à jour</button>
                            </div>
                        </div>
                        <div className="um-field um-field-full">
                            <label>Fichier</label>
                            <label className="um-file-drop">
                                <FileSpreadsheet size={18} style={{ color: '#2a5ca9' }} />
                                <span>{fileName || "Cliquez pour choisir un fichier Excel"}</span>
                                <input type="file" accept=".xlsx,.xls" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
                            </label>
                        </div>
                        <div className="um-field um-field-full">
                            <label>Colonnes remuneration acceptees</label>
                            <small>
                                Pour alimenter les virements depuis la table remuneration, ajoutez dans l'Excel :
                                remunerationRib, remunerationBanque, remunerationAgence, remunerationTaux.
                            </small>
                        </div>
                    </div>
                    <button className="um-btn um-btn-outline w-100 mt-3" onClick={() => dispatch(reviewUsersMassImportRequest(currentCompanyId, bulkAction, rows))} disabled={!fileName}>
                        Vérifier le fichier
                    </button>
                </div>
                {massImportReview && (
                    <div className="um-form-section">
                        <h3 className="um-form-title">Résultat</h3>
                        {!hasErrors ? (
                            <div className="um-alert um-alert-success"><CheckCircle size={16} /> Prêt pour l'import ({rows.length} lignes)</div>
                        ) : (
                            <div className="um-alert um-alert-danger">
                                <XCircle size={16} />
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {massImportReview.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
            <ScrollToTop />
        </div>
    );
}
